import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  validatePassword,
  signOut,
  verifyPasswordResetCode,
  confirmPasswordReset
} from "firebase/auth";

import {
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";

import { auth, db } from "../firebaseClient";

/*
|--------------------------------------------------------------------------
| LOGIN
|--------------------------------------------------------------------------
*/

export async function loginAndResolvePasswordState(email, password) {
  const normalizedEmail = email.trim().toLowerCase();

  const result = await signInWithEmailAndPassword(
    auth,
    normalizedEmail,
    password
  );

  const user = result.user;

  const profileRef = doc(db, "users", user.uid);
  const profileSnap = await getDoc(profileRef);
  const profile = profileSnap.exists() ? profileSnap.data() : null;

  return {
    uid: user.uid,
    email: user.email,
    role: profile?.role || null,
    mustChangePassword: profile?.mustChangePassword === true
  };
}

/*
|--------------------------------------------------------------------------
| CURRENT SESSION
|--------------------------------------------------------------------------
*/

export async function getCurrentSessionState() {
  const user = auth.currentUser;

  if (!user) {
    return null;
  }

  const profileRef = doc(db, "users", user.uid);
  const profileSnap = await getDoc(profileRef);
  const profile = profileSnap.exists() ? profileSnap.data() : null;

  return {
    uid: user.uid,
    email: user.email,
    role: profile?.role || null,
    mustChangePassword: profile?.mustChangePassword === true
  };
}

/*
|--------------------------------------------------------------------------
| FORGOT PASSWORD
|--------------------------------------------------------------------------
*/

export async function requestPasswordResetByEmail(email) {
  const normalizedEmail = email.trim().toLowerCase();

  auth.languageCode = "tr";

  await sendPasswordResetEmail(auth, normalizedEmail, {
    url: `${window.location.origin}/`,
    handleCodeInApp: false
  });
}

/*
|--------------------------------------------------------------------------
| FORCED RESET MAIL
|--------------------------------------------------------------------------
*/

export async function sendResetMailForCurrentUser() {
  const user = auth.currentUser;

  if (!user?.email) {
    throw new Error("Aktif kullanıcı veya email bulunamadı.");
  }

  auth.languageCode = "tr";

  await sendPasswordResetEmail(auth, user.email, {
    url: `${window.location.origin}/`,
    handleCodeInApp: false
  });

  return true;
}

/*
|--------------------------------------------------------------------------
| PASSWORD POLICY
|--------------------------------------------------------------------------
*/

export async function checkPasswordPolicy(password) {
  return await validatePassword(auth, password);
}

/*
|--------------------------------------------------------------------------
| VERIFY PASSWORD RESET CODE
|--------------------------------------------------------------------------
*/

export async function verifyResetCode(oobCode) {
  return await verifyPasswordResetCode(auth, oobCode);
}

/*
|--------------------------------------------------------------------------
| APPLY PASSWORD RESET
|--------------------------------------------------------------------------
*/

export async function applyPasswordReset(oobCode, newPassword) {
  await confirmPasswordReset(auth, oobCode, newPassword);

  return true;
}

/*
|--------------------------------------------------------------------------
| LOGIN AUTOMATICALLY AFTER PASSWORD RESET
|--------------------------------------------------------------------------
|
| ResetPasswordPage şifreyi başarıyla değiştirdikten sonra bu fonksiyon
| aynı origin üzerinde yeni şifreyle kullanıcıyı tekrar authenticate eder.
|
| Böylece kullanıcının web.app login ekranında manuel olarak tekrar giriş
| yapmasına gerek kalmaz.
|
*/

export async function loginAfterPasswordReset(email, password) {
  const normalizedEmail = email.trim().toLowerCase();

  const result = await signInWithEmailAndPassword(
    auth,
    normalizedEmail,
    password
  );

  return result.user;
}

/*
|--------------------------------------------------------------------------
| COMPLETE FORCED PASSWORD RESET
|--------------------------------------------------------------------------
|
| Kullanıcı yeni şifresiyle authenticate edildikten sonra Firestore'daki
| mustChangePassword alanı false yapılır.
|
*/

export async function completeForcedResetAfterLogin() {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("Aktif kullanıcı bulunamadı.");
  }

  const profileRef = doc(db, "users", user.uid);
  const profileSnap = await getDoc(profileRef);

  if (!profileSnap.exists()) {
    throw new Error("Kullanıcı profili bulunamadı.");
  }

  await updateDoc(profileRef, {
    mustChangePassword: false,
    passwordResetRequestedAt: null
  });

  /*
   * Güncellemenin gerçekten gerçekleştiğini doğruluyoruz.
   */
  const updatedSnap = await getDoc(profileRef);
  const updatedProfile = updatedSnap.data();

  if (updatedProfile?.mustChangePassword === true) {
    throw new Error(
      "Şifre değiştirildi ancak zorunlu şifre değiştirme durumu güncellenemedi."
    );
  }

  return true;
}

/*
|--------------------------------------------------------------------------
| LOGOUT
|--------------------------------------------------------------------------
*/

export async function logoutUser() {
  await signOut(auth);
}